import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,

} from '@material-ui/core';


import FolderIcon from '@material-ui/icons/Folder';
import GetAppIcon from '@material-ui/icons/GetApp';
import DeleteIcon from '@material-ui/icons/Delete';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import MoveToInboxIcon from '@material-ui/icons/MoveToInbox'; // move icon
import { useState, useMemo } from 'react';
import { useApi, discoveryApiRef, identityApiRef, alertApiRef } from '@backstage/core-plugin-api';

export interface Folder {
  id: string;
  name: string;
  parent_id: string | null;
  created_by: string;
  created_at: string;
  is_public?: boolean;   // ← added: result folders can be public/private
}

export interface Artifact {
  id: string;
  name: string;
  description: string;
  artifact_type: 'binary_file' | 'tabular_data' | 'document' | 'source_code' | 'image' | 'configuration' | 'model_weights' | 'dataset';
  category: string;
  use_case_tag: string | null;
  folder_id: string | null;
  file_name: string;
  file_size: number;
  mime_type: string;
  is_public: boolean;
  uploaded_by: string;
  uploaded_at: string;
}

interface Props {
  artifacts: Artifact[];
  folders: Folder[];
  currentUserRef: string;
  isAdmin: boolean;
  isMaintainer: boolean;
  isDeveloper: boolean;
  isGuest: boolean;
  onDeleted: () => void;
  onFolderClick: (folderId: string | null, folderName: string) => void;
}

const ARTIFACT_TYPE_LABELS: Record<string, string> = {
  binary_file: 'Binary File',
  tabular_data: 'Tabular Data',
  document: 'Document',
  source_code: 'Source Code',
  image: 'Image',
  configuration: 'Configuration',
  model_weights: 'Model Weights',
  dataset: 'Dataset',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function shortRef(ref: string): string {
  return ref.split('/').pop() ?? ref;
}

const ROOT_TARGET_VALUE = '__root__';
export const ROOT_TARGET_LABEL = 'Root (top-level)';

export const ArtifactTable = ({
  artifacts,
  folders,
  currentUserRef,
  onDeleted,
  onFolderClick,
}: Props) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi  = useApi(identityApiRef);
  const alertApi     = useApi(alertApiRef);

  // Track which folder IDs are currently downloading
  const [downloadingFolders, setDownloadingFolders] = useState<Set<string>>(new Set());
  // Track which folder IDs are having visibility toggled
  const [togglingFolders, setTogglingFolders] = useState<Set<string>>(new Set());
  // Local optimistic visibility state for folders
  const [folderVisibility, setFolderVisibility] = useState<Record<string, boolean>>({});

  // State for move‑folder dialog
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [folderBeingMoved, setFolderBeingMoved] = useState<Folder | null>(null);
  const [moveTargetFolderValue, setMoveTargetFolderValue] = useState<string | null>(null);

  // State for moving artifacts
  const [artifactBeingMoved, setArtifactBeingMoved] = useState<Artifact | null>(null);

  // UI state for move dialog enhancements
  const [moveLoading, setMoveLoading] = useState(false);
  // Search functionality removed; no search term state

  // Helper to build a map of folder id -> folder record for quick lookup
  const folderMap = useMemo(() => {
    const map: Record<string, Folder> = {};
    folders.forEach(f => { map[f.id] = f; });
    return map;
  }, [folders]);

  const folderChildrenMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    folders.forEach(folder => {
      if (!folder.parent_id) return;
      if (!map[folder.parent_id]) {
        map[folder.parent_id] = [];
      }
      map[folder.parent_id].push(folder.id);
    });
    return map;
  }, [folders]);

  const getFolderDescendantIds = (folderId: string): Set<string> => {
    const descendants = new Set<string>();
    const stack = [...(folderChildrenMap[folderId] ?? [])];

    while (stack.length > 0) {
      const currentId = stack.pop();
      if (!currentId || descendants.has(currentId)) continue;
      descendants.add(currentId);
      stack.push(...(folderChildrenMap[currentId] ?? []));
    }

    return descendants;
  };

  const selectedTargetFolderId = moveTargetFolderValue === ROOT_TARGET_VALUE
    ? null
    : moveTargetFolderValue;

  const currentMoveParentId = folderBeingMoved
    ? folderBeingMoved.parent_id
    : artifactBeingMoved
      ? artifactBeingMoved.folder_id
      : null;

  const hasSelectedMoveTarget = moveTargetFolderValue !== null;
  const isNoopMove = hasSelectedMoveTarget && selectedTargetFolderId === currentMoveParentId;
  const moveDialogCanConfirm = !moveLoading && hasSelectedMoveTarget && !isNoopMove;

  // Compute depth of a folder (number of ancestors) for indentation
  const getFolderDepth = (folder: Folder, visited = new Set<string>()): number => {
    let depth = 0;
    let current = folder;
    while (current.parent_id) {
      if (visited.has(current.id)) break; // safety against cycles
      visited.add(current.id);
      const parent = folderMap[current.parent_id];
      if (!parent) break;
      depth += 1;
      current = parent;
    }
    return depth;
  };

  // Filtered folders for the dialog (exclude the folder being moved and apply search)
  const filteredFolders = useMemo(() => {
    const descendantIds = folderBeingMoved ? getFolderDescendantIds(folderBeingMoved.id) : new Set<string>();

    return folders.filter(f => {
      // Exclude the folder being moved itself
      if (folderBeingMoved && (f.id === folderBeingMoved.id || descendantIds.has(f.id))) return false;
      // Hide only explicitly private folders not owned by the current user.
      if (f.is_public === false && f.created_by !== currentUserRef) return false;
      return true;
    });
  }, [folders, folderBeingMoved, currentUserRef, folderChildrenMap]);

  // Open dialog for a specific folder – also clear any artifact state
  const openMoveDialog = (folder: Folder) => {
    setFolderBeingMoved(folder);
    setArtifactBeingMoved(null); // ensure artifact state is cleared
    setMoveTargetFolderValue(null); // start with no explicit selection
    setMoveDialogOpen(true);
  };

  // Close dialog
  const closeMoveDialog = () => {
    setMoveDialogOpen(false);
    setFolderBeingMoved(null);
    setArtifactBeingMoved(null);
    setMoveTargetFolderValue(null);
  };

  // Confirm move – performs backend request and optimistic UI update
  const confirmMoveFolder = async () => {
    if (!folderBeingMoved) return;
    setMoveLoading(true);
    try {
      const baseUrl   = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      const response  = await fetch(`${baseUrl}/folders/${folderBeingMoved.id}/move`, {
        method:  'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ new_parent_id: selectedTargetFolderId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error ?? 'Move failed');
      }
      alertApi.post({ message: `Folder "${folderBeingMoved.name}" moved successfully.`, severity: 'success' });
      // Optimistic UI: if the folder no longer belongs to the current view, remove it locally
      // The component receives a flat list of folders for the current parent. If the new parent
      // differs from the current parent (which is the folderBeingMoved.parent_id), we can hide it.
      if (folderBeingMoved.parent_id !== selectedTargetFolderId) {
        // Remove the moved folder from the displayed list without waiting for a full refresh.
        // The parent component will re‑fetch via onDeleted, but we give immediate feedback.
        onDeleted();
      } else {
        // Same parent – just refresh to get any server‑side changes.
        onDeleted();
      }
    } catch (err: any) {
      alertApi.post({ message: `Move failed: ${err.message}`, severity: 'error' });
    } finally {
      setMoveLoading(false);
      closeMoveDialog();
    }
  };

  // Confirm move for artifact – similar loading handling
  const confirmMoveArtifact = async () => {
    if (!artifactBeingMoved) return;
    setMoveLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      const response = await fetch(`${baseUrl}/artifacts/${artifactBeingMoved.id}/move`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ new_folder_id: selectedTargetFolderId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error ?? 'Move failed');
      }
      alertApi.post({ message: `Artifact "${artifactBeingMoved.name}" moved successfully.`, severity: 'success' });
      onDeleted(); // refresh list
    } catch (err: any) {
      alertApi.post({ message: `Move failed: ${err.message}`, severity: 'error' });
    } finally {
      setMoveLoading(false);
      closeMoveDialog();
    }
  };

  // Wrapper to confirm move for either folder or artifact
  const confirmMove = async () => {
    if (!moveDialogCanConfirm) return;
    if (artifactBeingMoved) {
      await confirmMoveArtifact();
    } else {
      await confirmMoveFolder();
    }
  };

  // ── Artifact actions ────────────────────────────────────────────────────

  const handleDownload = async (artifact: Artifact) => {
    try {
      const baseUrl   = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      const response  = await fetch(`${baseUrl}/artifacts/${artifact.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);
      const blob = await response.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = artifact.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alertApi.post({ message: `Download failed: ${err.message}`, severity: 'error' });
    }
  };

  const handleDelete = async (artifact: Artifact) => {
    if (!window.confirm(`Delete "${artifact.name}"? This cannot be undone.`)) return;
    try {
      const baseUrl   = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      const response  = await fetch(`${baseUrl}/artifacts/${artifact.id}`, {
        method:  'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok && response.status !== 204) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error ?? 'Delete failed');
      }
      alertApi.post({ message: `"${artifact.name}" deleted.`, severity: 'success' });
      onDeleted();
    } catch (err: any) {
      alertApi.post({ message: `Delete failed: ${err.message}`, severity: 'error' });
    }
  };

  const handleToggleVisibility = async (artifact: Artifact) => {
    try {
      const baseUrl   = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      const response  = await fetch(`${baseUrl}/artifacts/${artifact.id}/visibility`, {
        method:  'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error ?? 'Visibility update failed');
      }
      const body = await response.json().catch(() => ({}));
      alertApi.post({
        message:  body.is_public ? 'Artifact is now public.' : 'Artifact is now private.',
        severity: 'success',
      });
      onDeleted();
    } catch (err: any) {
      alertApi.post({ message: `Visibility update failed: ${err.message}`, severity: 'error' });
    }
  };

  // ── Folder actions ──────────────────────────────────────────────────────

  const handleDeleteFolder = async (folder: Folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? It must be empty.`)) return;
    try {
      const baseUrl   = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      const response  = await fetch(`${baseUrl}/folders/${folder.id}`, {
        method:  'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok && response.status !== 204) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error ?? 'Delete failed');
      }
      alertApi.post({ message: `Folder "${folder.name}" deleted.`, severity: 'success' });
      onDeleted();
    } catch (err: any) {
      alertApi.post({ message: `Delete failed: ${err.message}`, severity: 'error' });
    }
  };

  // Download all contents of a folder as a ZIP
  const handleDownloadFolder = async (folder: Folder) => {
    setDownloadingFolders(prev => new Set(prev).add(folder.id));
    try {
      const baseUrl   = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      const response  = await fetch(`${baseUrl}/folders/${folder.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error ?? `Download failed (${response.status})`);
      }
      const blob     = await response.blob();
      const url      = URL.createObjectURL(blob);
      const a        = document.createElement('a');
      a.href         = url;
      a.download     = `${folder.name}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alertApi.post({ message: `Folder download failed: ${err.message}`, severity: 'error' });
    } finally {
      setDownloadingFolders(prev => {
        const next = new Set(prev);
        next.delete(folder.id);
        return next;
      });
    }
  };

  // Toggle a folder's public/private visibility
  const handleToggleFolderVisibility = async (folder: Folder) => {
    setTogglingFolders(prev => new Set(prev).add(folder.id));
    try {
      const baseUrl   = await discoveryApi.getBaseUrl('persistence');
      const { token } = await identityApi.getCredentials();
      const response  = await fetch(`${baseUrl}/folders/${folder.id}/visibility`, {
        method:  'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error ?? 'Visibility update failed');
      }
      const body = await response.json().catch(() => ({}));
      // Optimistic local update so the icon flips immediately without a full reload
      setFolderVisibility(prev => ({ ...prev, [folder.id]: body.is_public }));
      alertApi.post({
        message:  body.is_public
          ? `Folder "${folder.name}" is now public.`
          : `Folder "${folder.name}" is now private.`,
        severity: 'success',
      });
      // Also trigger parent refresh so the folder list stays in sync
      onDeleted();
    } catch (err: any) {
      alertApi.post({ message: `Visibility update failed: ${err.message}`, severity: 'error' });
    } finally {
      setTogglingFolders(prev => {
        const next = new Set(prev);
        next.delete(folder.id);
        return next;
      });
    }
  };

  // Move folder – open modal
  const handleMoveFolder = async (folder: Folder) => {
    // Open the move‑folder dialog; permission check is handled in the UI.
    openMoveDialog(folder);
  };

  // Open dialog for moving an artifact
  const openMoveArtifactDialog = (artifact: Artifact) => {
    setArtifactBeingMoved(artifact);
    setFolderBeingMoved(null);
    setMoveTargetFolderValue(null);
    setMoveDialogOpen(true);
  };

  // Helper to handle move folder click without propagating to row
  const handleMoveFolderClick = (e: React.MouseEvent, folder: Folder) => {
    e.stopPropagation();
    // stopImmediatePropagation may not exist on synthetic events, guard it
    // @ts-ignore
    if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
      // @ts-ignore
      e.nativeEvent.stopImmediatePropagation();
    }
    handleMoveFolder(folder);
  };

  // Helper to handle move artifact click without propagating to row
  const handleMoveArtifactClick = (e: React.MouseEvent, artifact: Artifact) => {
    e.stopPropagation();
    // @ts-ignore
    if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
      // @ts-ignore
      e.nativeEvent.stopImmediatePropagation();
    }
    openMoveArtifactDialog(artifact);
  };

  // ── Empty state ─────────────────────────────────────────────────────────

  if (artifacts.length === 0 && folders.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary" style={{ padding: 16 }}>
        This folder is empty. Use the Upload button to add an artifact or create a new folder.
      </Typography>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Visibility</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Use Case</strong></TableCell>
              <TableCell><strong>Filename</strong></TableCell>
              <TableCell><strong>Size</strong></TableCell>
              <TableCell><strong>Uploaded by</strong></TableCell>
              <TableCell><strong>Uploaded at</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>

            {/* ── Folder rows ── */}
            {folders.map(folder => {
              const canDeleteFolder      = folder.created_by === currentUserRef;
              // Simple rule: explicitly private folders are owner-only; all others are visible/movable.
              const isFolderPrivate = folder.is_public === false;
              const canMoveFolder = !isFolderPrivate || folder.created_by === currentUserRef;
              // Keep folder visibility behavior aligned with backend route:
              // only the folder owner can toggle visibility.
              const canManageFolder      = folder.created_by === currentUserRef;
              const isDownloadingFolder  = downloadingFolders.has(folder.id);
              const isTogglingFolder     = togglingFolders.has(folder.id);

              // Resolved visibility: local optimistic state wins, falls back to
              // what the server returned (folder.is_public), then defaults to false.
              const folderIsPublic =
                folder.id in folderVisibility
                  ? folderVisibility[folder.id]
                  : (folder.is_public ?? false);

              return (
                <TableRow
                  key={`folder-${folder.id}`}
                  hover
                  style={{ cursor: 'pointer' }}
                  onClick={() => onFolderClick(folder.id, folder.name)}
                >
                  {/* Name */}
                  <TableCell>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FolderIcon fontSize="small" color="action" />
                      <strong>{folder.name}</strong>
                    </span>
                  </TableCell>

                  {/* Type */}
                  <TableCell>
                    <Chip label="Folder" size="small" variant="outlined" />
                  </TableCell>

                  {/* Visibility */}
                  <TableCell>
                    {folderIsPublic
                      ? <Chip label="Public"  size="small" color="primary"  style={{ height: 20, fontSize: 11 }} />
                      : <Chip label="Private" size="small" variant="outlined" style={{ height: 20, fontSize: 11 }} />}
                  </TableCell>

                  {/* Unused columns */}
                  <TableCell>—</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>—</TableCell>

                  {/* Created by */}
                  <TableCell>{shortRef(folder.created_by)}</TableCell>

                  {/* Created at */}
                  <TableCell>{formatDate(folder.created_at)}</TableCell>

                  {/* Actions — stop row click propagation */}
                  <TableCell
                    align="center"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={e => e.stopPropagation()}
                  >
                    {/* Download folder as ZIP */}
                    <Tooltip title={isDownloadingFolder ? 'Downloading…' : 'Download folder as ZIP'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isDownloadingFolder}
                          onClick={() => handleDownloadFolder(folder)}
                        >
                          {isDownloadingFolder
                            ? <CircularProgress size={16} />
                            : <GetAppIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>

                    {/* Toggle visibility (owner only) */}
                    {canManageFolder && (
                      <Tooltip title={
                        isTogglingFolder
                          ? 'Updating…'
                          : folderIsPublic
                          ? 'Make private'
                          : 'Make public'
                      }>
                        <span>
                          <IconButton
                            size="small"
                            disabled={isTogglingFolder}
                            onClick={() => handleToggleFolderVisibility(folder)}
                          >
                            {isTogglingFolder
                              ? <CircularProgress size={16} />
                              : folderIsPublic
                              ? <LockOpenIcon fontSize="small" />
                              : <LockIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    {/* Delete folder */}
                    {canDeleteFolder && (
                      <Tooltip title="Delete folder (must be empty)">
                        <IconButton size="small" onClick={() => handleDeleteFolder(folder)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Move folder */}
                    {canMoveFolder && (
                      <Tooltip title="Move folder">
                        <IconButton size="small" onClickCapture={e => { e.stopPropagation(); e.preventDefault(); handleMoveFolderClick(e, folder); }}>
                          <MoveToInboxIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {/* ── Artifact rows ── */}
            {artifacts.map(artifact => {
              const canDelete = artifact.uploaded_by === currentUserRef;
              const canToggleVisibility = artifact.uploaded_by === currentUserRef;
              const canMoveArtifact = artifact.uploaded_by === currentUserRef;
              return (
                <TableRow key={artifact.id} hover>
                  <TableCell>
                    <Tooltip title={artifact.description || ''} placement="top-start">
                      <span>{artifact.name}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ARTIFACT_TYPE_LABELS[artifact.artifact_type] ?? artifact.artifact_type}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {artifact.is_public
                      ? <Chip label="Public"  size="small" color="primary"  style={{ height: 20, fontSize: 11 }} />
                      : <Chip label="Private" size="small" variant="outlined" style={{ height: 20, fontSize: 11 }} />}
                  </TableCell>
                  <TableCell>{artifact.category || '—'}</TableCell>
                  <TableCell>{artifact.use_case_tag || '—'}</TableCell>
                  <TableCell style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={artifact.file_name}>
                      <span>{artifact.file_name}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{formatBytes(artifact.file_size)}</TableCell>
                  <TableCell>{shortRef(artifact.uploaded_by)}</TableCell>
                  <TableCell>{formatDate(artifact.uploaded_at)}</TableCell>
                  <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => handleDownload(artifact)}>
                        <GetAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canToggleVisibility && (
                      <Tooltip title={artifact.is_public ? 'Make private' : 'Make public'}>
                        <IconButton size="small" onClick={() => handleToggleVisibility(artifact)}>
                          {artifact.is_public
                            ? <LockOpenIcon fontSize="small" />
                            : <LockIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(artifact)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canMoveArtifact && (
                      <Tooltip title="Move artifact">
                        <IconButton size="small" onClick={e => handleMoveArtifactClick(e, artifact)}>
                          <MoveToInboxIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

          </TableBody>
        </Table>
      </TableContainer>

      {/* Move Folder Dialog */}
      <Dialog key={folderBeingMoved?.id || artifactBeingMoved?.id || 'move-dialog'} open={moveDialogOpen} onClose={closeMoveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{folderBeingMoved ? `Move Folder "${folderBeingMoved.name}"` : artifactBeingMoved ? `Move Artifact "${artifactBeingMoved.name}"` : ''}</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth margin="dense" style={{ marginTop: 8 }}>
            <Typography variant="caption" color="textSecondary" style={{ marginBottom: 6, display: 'block' }}>
              Destination folder
            </Typography>
            <Select
              value={moveTargetFolderValue ?? ''}
              onChange={e => setMoveTargetFolderValue(e.target.value as string)}
              displayEmpty
              renderValue={(selected: any) => {
                if (!selected) return <em style={{ color: 'rgba(255, 255, 255, 0.54)' }}>Select target folder</em>;
                if (selected === ROOT_TARGET_VALUE) return <em>{ROOT_TARGET_LABEL}</em>;
                return folderMap[selected]?.name ?? selected;
              }}
            >
              <MenuItem value="" disabled>
                <em>Select target folder</em>
              </MenuItem>
              <MenuItem value={ROOT_TARGET_VALUE}>
                <em>{ROOT_TARGET_LABEL}</em>
              </MenuItem>
              {filteredFolders.map(f => {
                const depth = getFolderDepth(f);
                const indent = '\u00A0'.repeat(depth * 4);
                return (
                  <MenuItem key={f.id} value={f.id}>
                    {indent}{f.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMoveDialog} color="default" disabled={moveLoading}>Cancel</Button>
          <Button
            onClick={confirmMove}
            color="primary"
            variant="contained"
            disabled={!moveDialogCanConfirm}
          >
            {moveLoading ? <CircularProgress size={20} /> : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};